import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'

import Button from '@eig-builder/core-ui/Button'
import TextField from '@eig-builder/core-ui/TextField'

import { DataElementLocations } from '@eig-builder/core-utils/helpers/tagging-helper'

import { NotificationBarContext } from '@eig-builder/module-notification-bar-with-context'
import { showSuccess } from '@eig-builder/module-notification-bar-with-context/actions'

import ButtonWithLoader from 'core/components/button-with-loader'
import Modal from 'core/components/modal'

import Text from '@eig-builder/module-localization'
import './lang'

const styles = theme => ({
  content: {
    paddingTop: theme.spacing(2)
  },
  fieldWrapper: {
    marginTop: theme.spacing(4)
  },
  errorText: {
    color: theme.palette.error.main
  },
  actions: {
    margin: `${theme.spacing(4)}px`
  }
})

class AccountRelink extends React.Component {
  static contextType = NotificationBarContext

  state = {
    accountId: null,
    currentSoid: null,
    destinationSoid: null,
    destinationUserId: null,
    accountIdError: null,
    currentSoidError: null,
    destinationSoidError: null,
    destinationUserIdError: null
  }

  componentDidUpdate (prevProps) {
    if (prevProps.posting && !this.props.posting && this.props.response && this.props.response.status_code === 200) {
      showSuccess('The account has been successfully relinked.')(this.context.dispatch)
      this.resetState()
      setTimeout(() => {
        this.handleClose()
      }, 2000)
    }
  }

  handleAccountIdChange = event => {
    const accountId = parseInt(event.target.value)
    if (Number.isNaN(accountId)) {
      return this.setState({
        accountId: null,
        accountIdError: 'Please enter a valid number.'
      })
    }

    this.setState({
      accountId,
      accountIdError: null
    })
  }

  handleCurrentSoidChange = event => {
    const currentSoid = parseInt(event.target.value)

    if (Number.isNaN(currentSoid)) {
      return this.setState({
        currentSoid: null,
        currentSoidError: 'Please enter a valid number.'
      })
    }

    this.setState({
      currentSoid: currentSoid,
      currentSoidError: null
    })
  }

  handleDestinationSoidChange = event => {
    const destinationSoid = parseInt(event.target.value)

    if (Number.isNaN(destinationSoid)) {
      return this.setState({
        destinationSoid: null,
        destinationSoidError: 'Please enter a valid number.'
      })
    }

    this.setState({
      destinationSoid: destinationSoid,
      destinationSoidError: null
    })
  }

  handleDestinationUserIdChange = event => {
    const destinationUserId = event.target.value

    if (typeof destinationUserId !== 'string') {
      return this.setState({
        destinationUserId: null,
        destinationSoidError: 'Please enter a valid user id.'
      })
    }

    this.setState({
      destinationUserId: event.target.value,
      destinationSoidError: null
    })
  }

  resetState = () => {
    this.setState({
      accountId: null,
      currentSoid: null,
      destinationSoid: null,
      destinationUserId: null,
      accountIdError: null,
      currentSoidError: null,
      destinationSoidError: null,
      destinationUserIdError: null
    })
  }

  onSubmit = () => {
    const { account } = this.props
    const data = {
      account_id: this.state.accountId,
      current_soid: this.state.currentSoid,
      destination_soid: this.state.destinationSoid,
      destination_userid: this.state.destinationUserId
    }

    this.props.accountRelink(data, account.id, account.partnerId)
  }

  handleClose = () => {
    this.resetState()
    this.props.onClose()
  }

  render () {
    const { classes, open, posting } = this.props

    const actions = [
      <Button
        key='account-relink-cancel'
        color='secondary'
        disabled={false}
        onClick={this.handleClose}
        dataElementLocation={DataElementLocations.BODY}
        dataElementLabel='cst-account-details-account-relink-cancel'
        dataElementId='cst-account-details-account-relink-cancel'
      >
        <Text message='modules.crm.components.account.accountRelink.cancel' />
      </Button>,
      <ButtonWithLoader
        key='account-relink-submit'
        disabled={this.state.accountIdError || this.state.currentSoidError || this.state.destinationSoidError || this.state.destinationUserIdError ||
        (!this.state.accountId || !this.state.currentSoid || !this.state.destinationSoid || !this.state.destinationUserId)}
        onClick={this.onSubmit}
        dataElementSuffix='account-details--account-relink-confirm'
        showLoader={!!posting}
        text={<Text message='modules.crm.components.account.accountRelink.confirm' />}
      />
    ]

    return (
      <Modal
        actions={actions}
        dataElementPrefix='cst-account--account-relink'
        disableBackdropClick
        handleClose={this.handleClose}
        open={open}
        title={<Text message='modules.crm.components.account.accountRelink.title' />}
      >
        <div className={classes.fieldWrapper}>
          <TextField
            fullWidth
            label={<Text message='modules.crm.components.account.accountRelink.labels.accountId' />}
            placeholder='Enter your account id...'
            value={this.state.accountId}
            error={this.state.accountIdError}
            helperText={this.state.accountIdError}
            onChange={this.handleAccountIdChange}
            variant='outlined'
            dataElementLocation={DataElementLocations.BODY}
            dataElementLabel='cst-account-account-relink-accountId-description'
            dataElementId='cst-account-account-relink-accountId-description'
          />
        </div>
        <div className={classes.fieldWrapper}>
          <TextField
            fullWidth
            label={<Text message='modules.crm.components.account.accountRelink.labels.currentSoid' />}
            placeholder='Enter your current soID...'
            value={this.state.currentSoid}
            error={this.state.currentSoidError}
            helperText={this.state.currentSoidError}
            onChange={this.handleCurrentSoidChange}
            variant='outlined'
            dataElementLocation={DataElementLocations.BODY}
            dataElementLabel='cst-account-account-relink-currentSoid-description'
            dataElementId='cst-account-account-relink-currentSoid-description'
          />
        </div>
        <div className={classes.fieldWrapper}>
          <TextField
            fullWidth
            label={<Text message='modules.crm.components.account.accountRelink.labels.destinationSoid' />}
            placeholder='Enter your destination soID...'
            value={this.state.destinationSoid}
            error={this.state.destinationSoidError}
            helperText={this.state.destinationSoidError}
            onChange={this.handleDestinationSoidChange}
            variant='outlined'
            dataElementLocation={DataElementLocations.BODY}
            dataElementLabel='cst-account-account-relink-destinationSoid-description'
            dataElementId='cst-account-account-relink-destinationSoid-description'
          />
        </div>
        <div className={classes.fieldWrapper}>
          <TextField
            fullWidth
            label={<Text message='modules.crm.components.account.accountRelink.labels.destinationUserId' />}
            placeholder='Enter the destination user Id...'
            value={this.state.destinationUserId}
            error={this.state.destinationUserIdError}
            helperText={this.state.destinationUserIdError}
            onChange={this.handleDestinationUserIdChange}
            variant='outlined'
            dataElementLocation={DataElementLocations.BODY}
            dataElementLabel='cst-account-account-relink-accountId-description'
            dataElementId='cst-account-account-relink-accountId-description'
          />
        </div>
      </Modal>
    )
  }
}

AccountRelink.propTypes = {
  account: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  accountRelink: PropTypes.func.isRequired,
  posting: PropTypes.bool,
  response: PropTypes.object
}

export default withStyles(styles)(AccountRelink)
